/**
 * Width of a docked right-hand panel (comments, design theme, thumbnails) —
 * the pure half: bounds, the drag arithmetic, and the per-user memory.
 *
 * A PERSONAL VIEW PREFERENCE, exactly like dark mode (core/theme.ts) and the
 * brainstorm model pick: one localStorage key per panel, never the CRDT doc.
 * How wide you like your comments column is not board content, and syncing it
 * would make dragging a panel an edit everyone else sees.
 */

export interface PanelWidthBounds {
  min: number;
  max: number;
}

/** Narrow enough for a laptop, wide enough to read a thumbnail gallery 2-up. */
export const PANEL_MIN_WIDTH = 240;
export const PANEL_MAX_WIDTH = 760;
/** Board pixels a panel must always leave beside it — the board stays usable. */
export const PANEL_BOARD_RESERVE = 420;
/** One keyboard nudge on the handle. */
export const PANEL_KEY_STEP = 16;

const STORAGE_PREFIX = "kola.panelWidth.";

export const DEFAULT_PANEL_BOUNDS: PanelWidthBounds = { min: PANEL_MIN_WIDTH, max: PANEL_MAX_WIDTH };

export function clampPanelWidth(width: number, bounds: PanelWidthBounds = DEFAULT_PANEL_BOUNDS): number {
  if (!Number.isFinite(width)) return bounds.min;
  return Math.round(Math.min(bounds.max, Math.max(bounds.min, width)));
}

/**
 * The widest a panel may go on this window: the fixed ceiling, or whatever
 * leaves `PANEL_BOARD_RESERVE` of board, whichever is smaller — never below
 * the floor, so a tiny window still gets a usable panel rather than a sliver.
 */
export function boundsForWindow(windowWidth: number): PanelWidthBounds {
  const roomy = Math.floor(windowWidth - PANEL_BOARD_RESERVE);
  return { min: PANEL_MIN_WIDTH, max: Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, roomy)) };
}

/**
 * A docked-RIGHT panel widens as the pointer moves LEFT: the handle sits on
 * its left edge, so the width is the start width plus how far the pointer has
 * travelled toward the board.
 */
export function widthFromDrag(
  startWidth: number,
  startX: number,
  clientX: number,
  bounds: PanelWidthBounds = DEFAULT_PANEL_BOUNDS,
): number {
  return clampPanelWidth(startWidth + (startX - clientX), bounds);
}

/** The remembered width for one panel, or `fallback` (also on garbage). */
export function storedPanelWidth(
  key: string,
  fallback: number,
  bounds: PanelWidthBounds = DEFAULT_PANEL_BOUNDS,
): number {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (raw === null) return clampPanelWidth(fallback, bounds);
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? clampPanelWidth(parsed, bounds) : clampPanelWidth(fallback, bounds);
  } catch {
    return clampPanelWidth(fallback, bounds);
  }
}

export function rememberPanelWidth(key: string, width: number): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, String(Math.round(width)));
  } catch {
    // A browser that refuses storage still gets the width for this visit.
  }
}

export function forgetPanelWidth(key: string): void {
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    // Nothing to forget.
  }
}
